import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Layout, MenuProps, notification, Typography } from 'antd'
import { error } from 'src/app/themes/primaryThemeDefault'
import { DeleteIcon, EyeIcon, PenIcon } from 'src/assets/icons'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import DeleteImg from 'src/assets/images/delete-img.png'

// import './index.scss'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { useRolePermission } from 'src/app/shared/hooks/useRolePermission'
import { EPermissions, ESubModules } from 'src/app/shared/models/roleAndPermission.model'
import { usePayeeMatchParameter } from './usePayeeMatchParameters'
import { ConditionalTag } from './components/ConditionalTag'
import DeleteButton from 'src/app/shared/components/common/button/delete-button'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import ActionConfirmationModal from 'src/app/shared/components/modal/action-confirmation-modal'
import { getListPayeeMatchParameters } from 'src/app/shared/services/PayeeMatchParameters'
import NewCommonTable from 'src/app/shared/components/common/new-table'
import { IGetExportData } from 'src/app/shared/components/common/new-table/table-setting.model'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'

const PayeeMatchParameters: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location
  const { userPermissions } = useRolePermission()
  const [selectedProfileName, setSelectedProfileName] = useState<any>({})
  const [isModalConfirmDeleteOpened, setIsModalConfirmDeleteOpened] = useState<boolean>(false)
  const {
    isLoading,
    listPayeeMatchParameters,
    totalRecords,
    getDataPayeeMatchParameter,
    isDeleting,
    deletePayeeMatchParameters,
  } = usePayeeMatchParameter()

  const [api, contextHolder] = notification.useNotification()

  const getItems = useCallback(
    (rowData: any): MenuProps['items'] => {
      return [
        userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.VIEW_ITEMS)
          ? {
              key: '1',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('PayeeMatchParameters:landingScreen.actionValue.viewPayeeMatchParameters')}
                </Typography.Text>
              ),
              icon: <EyeIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.viewPayeeMatchParameters}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.ADD_EDIT_ITEMS) && !rowData?.isDefault
          ? {
              key: '2',
              label: (
                <Typography.Text style={{ fontSize: '14px' }}>
                  {t('PayeeMatchParameters:landingScreen.actionValue.editPayeeMatchParameters')}
                </Typography.Text>
              ),
              icon: <PenIcon />,
              onClick: () => {
                if (rowData?.id) navigate(`${menuUrls.editPayeeMatchParameters}/${rowData?.id}`)
              },
            }
          : null,
        userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.DELETE_ITEMS) && !rowData?.isDefault
          ? {
              key: '3',
              label: (
                <Typography.Text style={{ fontSize: '14px', color: error }}>
                  {t('PayeeMatchParameters:landingScreen.actionValue.deletePayeeMatchParameters')}
                </Typography.Text>
              ),
              icon: <DeleteIcon />,
              onClick: () => {
                if (rowData?.id) {
                  setSelectedProfileName(rowData)
                  setIsModalConfirmDeleteOpened(true)
                }
              },
            }
          : null,
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, navigate],
  )

  const generateCustomColumnDefs = useCallback(
    (colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> =>
      colDefs.map((item, index) => {
        if (item.colId === 'detectAdditionalNames') {
          return {
            ...item,
            dataType: 'date',
            cellRenderer: (props: any) => {
              const { data } = props
              return <ConditionalTag status={data?.[item.colId]} />
            },
          }
        }

        if (item.colId === 'createExceptionsForConfidenceScoresBelowDefault') {
          item.dataType = 'number'
        }

        return item
      }),
    [],
  )

  const handleAddRecord = () => {
    navigate(menuUrls.addPayeeMatchParameters)
  }

  const handleDeleteAccount = async () => {
    if (selectedProfileName?.id) {
      deletePayeeMatchParameters(selectedProfileName.id)
        .then(
          (res: any) => {
            if (res?.statusCode === SYSTEM_MESSAGE_IDS.CAN_NOT_DELETE_PAYEE_MATCH_PARAMETER) {
              api.error({
                message: t('common:error'),
                description: (
                  <RichTextField
                    richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.CAN_NOT_DELETE_PAYEE_MATCH_PARAMETER)}
                  />
                ),
              })
            } else {
              api.success({
                message: t('common:success'),
                description: (
                  <RichTextField
                    richText={t('common:delete_record_success', { record_name: selectedProfileName.profileName })}
                  />
                ),
              })
              outerTableRef.current?.handleSearchLogic?.({})
            }
          },
          (err) => {
            console.log('err', err)
          },
        )
        .finally(() => {
          setIsModalConfirmDeleteOpened(false)
        })
    }
  }

  const handleSearch = useCallback(
    async (payload: Partial<IGetExportData>) => {
      await getDataPayeeMatchParameter(payload as any)
    },
    [getDataPayeeMatchParameter],
  )

  useEffect(() => {
    if (state?.payeeMatchParameters?.message) {
      api.success({
        message: <RichTextField richText={state?.payeeMatchParameters?.message} />,
        closeIcon: null,
      })
    }
    window.history.replaceState({}, '')
  }, [api, state?.payeeMatchParameters])

  return (
    <Layout.Content style={{ padding: 24 }}>
      {contextHolder}

      <NewCommonTable
        ref={outerTableRef}
        rowData={listPayeeMatchParameters}
        total={totalRecords}
        translationNamespace='PayeeMatchParameters:landingScreen.tableColumns'
        tableSettingName={TABLE_NAME.PAYEE_MATCH_PARAMETERS}
        displayAddBtn={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.ADD_EDIT_ITEMS)}
        displayDownloadBtn={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.VIEW_ITEMS)}
        loading={isLoading}
        handleAddRecord={handleAddRecord}
        actionDropdownItems={getItems}
        handleSearch={handleSearch}
        handleDownload={async (payload) => await getListPayeeMatchParameters(payload)}
        pageName={TABLE_NAME.PAYEE_MATCH_PARAMETERS}
        generateCustomColumnDefs={generateCustomColumnDefs}
      />
      <ActionConfirmationModal
        icon={<img src={DeleteImg} alt='Confirm Icon' />}
        title={t('Modal:delete_record_title')}
        isOpen={isModalConfirmDeleteOpened}
        description={
          <RichTextField
            richText={
              t('PayeeMatchParameters:landingScreen.actionValue.deleteText', {
                profileName: selectedProfileName?.profileName,
              }) || ''
            }
          />
        }
        descriptionType='string'
        onClose={() => {
          setIsModalConfirmDeleteOpened(false)
        }}
        footer={[
          <Button
            size='middle'
            type='primary'
            key='btn-cancel'
            disabled={isDeleting}
            onClick={() => {
              setIsModalConfirmDeleteOpened(false)
            }}
          >
            {t('common:cancel')}
          </Button>,
          <DeleteButton key='btn-delete' loading={isDeleting} disabled={isDeleting} onClick={handleDeleteAccount}>
            {t('common:delete')}
          </DeleteButton>,
        ]}
      />
    </Layout.Content>
  )
}

export default PayeeMatchParameters
